import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import type { OrgStoryView } from "@/lib/orgStory";
import { hasPublicNews } from "@/lib/orgStory/sections";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import {
  articleDate,
  articleExcerpt,
  articleReadTime,
  contentTypeLabel,
  formatLongDate,
  organizationArticlePath,
} from "@/lib/publicOrganizationProfile";
import StorySection from "./StorySection";

/**
 * The public page's News section (spec 2026-09-24 §5.1): the newsroom and
 * awards from get_public_organization_profile. Never the social feed. The
 * signed-in page uses NewsUpdatesTab instead, which reads tables an
 * anonymous visitor cannot.
 */
export default function PublicNewsSection({ view }: { view: OrgStoryView }) {
  if (!hasPublicNews(view.news)) return null;
  const { articles, awards } = view.news!;
  const slug = view.identity.slug;

  return (
    <StorySection section="news">
      {articles.length > 0 && (
        <ul className="m-0 grid gap-4 p-0 sm:grid-cols-2">
          {articles.map((a) => {
            const date = articleDate(a);
            const summary = articleExcerpt(a);
            const readTime = articleReadTime(a);
            return (
              <li key={a.id} className="list-none">
                <article className="h-full">
                  <SurfaceCard className="flex h-full flex-col overflow-hidden rounded-2xl" padding="md">
                    {a.image_url && (
                      <img
                        src={a.image_url}
                        alt=""
                        loading="lazy"
                        className="-mx-6 -mt-6 mb-4 aspect-[16/9] w-[calc(100%+3rem)] max-w-none object-cover"
                      />
                    )}
                    <p className="eyebrow mb-2 text-[11px] text-gold-ink">{contentTypeLabel(a.type)}</p>
                    <h3 className="m-0 font-heading text-[17px] font-bold leading-[1.3] text-ink-900">
                      {slug ? (
                        <Link to={organizationArticlePath(slug, a.id)} className="hover:underline">
                          {a.title}
                        </Link>
                      ) : (
                        a.title
                      )}
                    </h3>
                    {summary && <p className="mt-2 text-[14px] leading-[1.6] text-ink-700">{summary}</p>}
                    <p className="mt-auto pt-3 text-[12.5px] text-ink-500">
                      {date && <time dateTime={date}>{formatLongDate(date)}</time>}
                      {date && readTime && " · "}
                      {readTime}
                    </p>
                  </SurfaceCard>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      {awards.length > 0 && (
        <SurfaceCard padding="lg" className="rounded-2xl">
          <h3 className="m-0 mb-3 font-heading text-base font-bold text-ink-900">Awards &amp; recognition</h3>
          <ul className="m-0 space-y-3 p-0">
            {awards.map((a) => (
              <li key={a.id} className="flex list-none items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-tint">
                  <Trophy className="h-4 w-4 text-featured" strokeWidth={1.8} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold leading-[1.3] text-ink-900">{a.name}</span>
                  <span className="block text-[12.5px] text-ink-500">
                    {a.org} · {a.year}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </SurfaceCard>
      )}
    </StorySection>
  );
}
